/**
 * Attachment preview utilities — file-type detection, viewer URL builder and
 * formatting helpers used by `DocumentPreviewPanel`.
 *
 * Adaptacja z szablonu SellCall (`apps/admin/src/components/document-preview/utils.ts`
 * w `ite_workflow365`) do kartoteki `RecordAttachmentDTO` tego repo (`@saas/shared`).
 * Różnice celowe:
 *  - `getPreviewKind` przyjmuje `mimeType` + opcjonalny `ext` wprost (zamiast obiektu
 *    dokumentu), bo DTO tego repo ma `mimeType`/`fileName`, nie `type`/`name`.
 *  - Brak builderów do zewnętrznych przeglądarek Office (MS/Google) — panel tego repo
 *    NIGDY nie osadza dokumentów `office` w zewnętrznym viewerze (zero wycieku URL-i
 *    załączników tenanta do stron trzecich); dla `office`/`video`/`audio`/`unknown`
 *    UI pokazuje wyłącznie przycisk pobrania.
 */

import {
  Archive,
  File,
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  type LucideIcon,
  Music,
} from 'lucide-react';

export type PreviewKind =
  | 'image'
  | 'pdf'
  | 'office'
  | 'xml'
  | 'text'
  | 'video'
  | 'audio'
  | 'unknown';

/** Rodzaje, które panel faktycznie renderuje inline (obraz / iframe). Reszta = tylko pobranie. */
export const EMBEDDABLE_PREVIEW_KINDS: ReadonlySet<PreviewKind> = new Set([
  'image',
  'pdf',
  'xml',
  'text',
]);

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const TEXT_EXTS = ['txt', 'csv', 'log', 'json', 'md'];
const VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'mov', 'm4v'];
const AUDIO_EXTS = ['mp3', 'wav', 'oga', 'm4a', 'aac', 'flac'];

/** Wyciąga rozszerzenie pliku (bez kropki, lowercase) z nazwy; `''` gdy brak. */
export function getFileExtension(fileName: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(fileName || '');
  return match?.[1] ? match[1].toLowerCase() : '';
}

/**
 * Rozstrzyga zgrubny rodzaj podglądu na podstawie MIME type z fallbackiem na
 * rozszerzenie pliku (część serwerów/uploadów zwraca `application/octet-stream`).
 */
export function getPreviewKind(mimeType: string, ext?: string): PreviewKind {
  const type = (mimeType || '').toLowerCase();
  const e = (ext ?? '').toLowerCase().replace(/^\./, '');

  if (type.startsWith('image/') || IMAGE_EXTS.includes(e)) return 'image';
  if (type === 'application/pdf' || e === 'pdf') return 'pdf';
  if (type === 'application/xml' || type === 'text/xml' || e === 'xml') return 'xml';
  if (
    type.includes('word') ||
    type.includes('excel') ||
    type.includes('powerpoint') ||
    type.includes('spreadsheet') ||
    type.includes('presentation') ||
    type.includes('officedocument') ||
    OFFICE_EXTS.includes(e)
  ) {
    return 'office';
  }
  if (type.startsWith('text/') || TEXT_EXTS.includes(e)) return 'text';
  if (type.startsWith('video/') || VIDEO_EXTS.includes(e)) return 'video';
  if (type.startsWith('audio/') || AUDIO_EXTS.includes(e)) return 'audio';
  return 'unknown';
}

/** Czy dany rodzaj jest renderowany inline przez panel (obraz/pdf/xml/tekst). */
export function isEmbeddablePreviewKind(kind: PreviewKind): boolean {
  return EMBEDDABLE_PREVIEW_KINDS.has(kind);
}

/**
 * Ścieżka (względna wobec `api` z `@/lib/api`, baseURL `/api`) do strumienia treści
 * załącznika. UWAGA: w scope tenanta autoryzacja idzie przez nagłówek
 * `Authorization: Bearer <access_token>` trzymany w JS (nie cookie) — dlatego tej
 * ścieżki NIE WOLNO wpinać bezpośrednio jako `<img src>`/`<iframe src>` (żądanie
 * przeglądarki nie dołączy Beare­ra i dostaniemy 401). Zawsze pobieraj przez
 * `api.get(buildAttachmentContentUrl(id), { responseType: 'blob' })`, a wynikowy
 * blob zamień na `URL.createObjectURL(...)` dopiero jako źródło dla elementu.
 */
export function buildAttachmentContentUrl(attachmentId: string): string {
  return `/attachments/${attachmentId}/content`;
}

/** Ikona lucide dopasowana do rodzaju podglądu (z rozróżnieniem arkusz/dokument dla `office`). */
export function getPreviewKindIcon(kind: PreviewKind, mimeType?: string, ext?: string): LucideIcon {
  const type = (mimeType || '').toLowerCase();
  const e = (ext ?? '').toLowerCase().replace(/^\./, '');

  switch (kind) {
    case 'image':
      return ImageIcon;
    case 'video':
      return Film;
    case 'audio':
      return Music;
    case 'office':
      if (
        type.includes('excel') ||
        type.includes('spreadsheet') ||
        ['xls', 'xlsx', 'csv'].includes(e)
      ) {
        return FileSpreadsheet;
      }
      return FileText;
    case 'pdf':
    case 'xml':
    case 'text':
      return FileText;
    default:
      if (type.includes('zip') || type.includes('archive') || ['zip', 'rar', '7z'].includes(e))
        return Archive;
      return File;
  }
}

/** Formatuje rozmiar pliku (B/KB/MB/GB), zgodnie z konwencją `RecordAttachments.tsx`. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
